import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';
import { sanitizeNoteInput, hasActualTextContent } from '../utils/sanitize';

interface BreachReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, targetType?: 'RESPONSE' | 'ONGOING' | 'RESOLUTION') => void;
  targetType?: 'RESPONSE' | 'ONGOING' | 'RESOLUTION';
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
}

const BreachReasonModal: React.FC<BreachReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  targetType,
  title,
  message,
  confirmLabel = 'Submit',
  cancelLabel = 'Cancel',
  isLoading = false
}) => {
  // Generate default title and message based on target type
  const targetTypeLabel = targetType ? targetType.charAt(0) + targetType.slice(1).toLowerCase() : '';
  const defaultTitle = targetType
    ? `Reason for ${targetTypeLabel} SLA Breach`
    : 'Reason for SLA Breach';
  const defaultMessage = targetType
    ? `The ${targetTypeLabel} SLA has been breached for this ticket. Please provide a reason explaining why the SLA was breached.`
    : 'SLA has been breached for this ticket. Please provide a reason explaining why the SLA was breached before resolving or closing.';

  const displayTitle = title || defaultTitle;
  const displayMessage = message || defaultMessage;
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    // Strip HTML tags and check if actual text content exists
    if (!hasActualTextContent(reason)) {
      setError('Breach reason is required');
      return;
    }
    onConfirm(sanitizeNoteInput(reason), targetType);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                        {displayTitle}
                      </Dialog.Title>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-2 ml-9">
                  <p className="text-sm text-gray-500 mb-4">
                    {displayMessage}
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Breach Reason <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={reason}
                      onChange={(value) => {
                        setReason(value);
                        setError('');
                      }}
                      placeholder="Explain why the SLA was breached (e.g., vendor delay, resource constraints, complexity of issue, etc.)..."
                      minHeight="200px"
                      disabled={isLoading}
                      className={error ? 'border-red-300' : ''}
                    />
                    {error && (
                      <p className="mt-1 text-sm text-red-600">{error}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    disabled={isLoading}
                  >
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Submitting...' : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BreachReasonModal;
